

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { RiskPlusReport } from '../entities/report/RiskPlusReport';
import { getRiskReportService } from '../services/RiskPlusReportService';
import AuthStore from './AuthStore';
import { DashboardStore } from './DashboardStore';
import UseCaseChatStore from './UseCaseChatStore';


export interface IRiskPlusReportState {
  riskPlusReport: RiskPlusReport | null;
  isLoading: boolean;
  loadReportData: (useCaseChatId: string) => Promise<RiskPlusReport>;
  setRiskPlusReport: (report: RiskPlusReport | null) => void;

  generateRiskPlusReport: (useCaseChatId: string) => Promise<{ jobId: string }>;
}

export class RiskPlusReportStore {
  static useRiskPlusReportStore = create<IRiskPlusReportState>()(
    devtools(
      subscribeWithSelector(
        (set /*, get*/) => {
          return {
            riskPlusReport: null,
            isLoading: false,

            async loadReportData(riskPlusReportId: string): Promise<RiskPlusReport> {
              // Simulate loading report data based on useCaseChatId
              // In a real implementation, this would likely involve an API call
              set({ isLoading: true });
              try {
                const riskReportService = getRiskReportService()
                const report = await riskReportService.getRiskReportById(riskPlusReportId);
                set({ riskPlusReport: report, isLoading: false });
                return report;
              } catch (error) {
                console.error("Error loading report data:", error);
                set({ riskPlusReport: null, isLoading: false });
                throw new Error("Unable to load report data");
              }
            },

            setRiskPlusReport: (report: RiskPlusReport | null) => {
              set({ riskPlusReport: report });
            },

            /** Trigger report generation  */
            async generateRiskPlusReport(useCaseChatId: string): Promise<{ jobId: string }> {

              try {
                const riskReportService = getRiskReportService();
                const job = await riskReportService.generateReports(useCaseChatId);

                // Trigger update of license
                await AuthStore.useAuthStore.getState().getLicense().then(licence => {
                  if (job.license.riskPlusReportCredits !== licence.riskPlusReportCredits) {
                    console.warn(`License riskPlusReportCredits (${licence.riskPlusReportCredits}) is out of sync with server value (${job.license.riskPlusReportCredits}) after report generation.`);
                  }
                  return licence;
                }).catch(error => {
                  console.error("Error updating license after report generation:", error);
                });

                // Refresh the UseCaseChat data (its processing status is updated)
                UseCaseChatStore.useUseCaseChatStore.getState().getUseCaseChatById(useCaseChatId).catch(error => {
                  console.error("Error refreshing use case chat after report generation:", error);
                });

                // TODO: Control polling for report generation result (get the job info from server)
                DashboardStore.useDashboardStore.getState().loadUseCaseBriefs(true); // trigger background update

                return job;

              } catch (error) {
                console.error("Error generating report:", error);
                const message = error instanceof Error && error.message ? error.message : "Unable to generate report";
                throw new Error(message);
              }
            }

          } // state object
        }// end of (set,get) 
      ),// end of subscribeWithSelector 
      { name: 'RiskPlusReportStore' }
    )// end of devtools
  ); // end of create

}

export default RiskPlusReportStore;
